import React, { useEffect, useState } from 'react';
import AvalanchePathImagesService, {
  AVALANCHE_PATH_IMAGES_PARAMS,
  AvalanchePathImagesDTO,
} from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesService';
import { Button, Col, Row } from 'react-bootstrap';
import AvalanchePathImagesEditModal from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesEditModal';
import { useAvalanchePathImagesContext } from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesContext';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import AvalanchePathImagesCreateModal from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesCreateModal';
import Authorize from '@components/Authorize';
import { Permissions } from '@awp/constants';
import AwpValidationErrorModal from '@components/Common/AwpValidationErrorModal';
import Pagination from '@components/Common/Pagination';
import { DEFAULT_PAGE_SIZE } from '@components/table';

const AvalanchePathImagesGrid = (props: any) => {
  const { readOnly, mode, ...rest } = props;

  const {
    parentEntityId,
    entity,
    setEntity,
    mediaEntities,
    filteredData,
    searchParams,
    search,
    eventErrors,
    setMediaEntities,
    setFilteredData,
    setSearchParams,
    setSearch,
    setEventErrors,
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    showCreateModal,
    setShowCreateModal,
    onHideEdit,
    onHandleEdit,
    onHideCreate,
    onHandleCreate,
    onDelete,
    loading,
    setLoading,
  } = useAvalanchePathImagesContext();

  const [sortBy, setSortBy] = useState(searchParams.get(AVALANCHE_PATH_IMAGES_PARAMS.SORT_BY) ?? 'desc');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [canPreviousPage, setCanPreviousPage] = useState(false);
  const [canNextPage, setCanNextPage] = useState(false);
  const [paginatedData, setPaginatedData] = useState<AvalanchePathImagesDTO[]>([]);

  const avalanchePathImagesService = new AvalanchePathImagesService();

  useEffect(() => {
    setPageCount(Math.ceil(filteredData.length / pageSize));

    const myFilteredData = filteredData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    setPaginatedData(myFilteredData);
  }, [filteredData, pageSize]);

  useEffect(() => {
    setCanPreviousPage(pageIndex > 0);
    setCanNextPage(pageIndex < pageCount - 1);
  }, [pageIndex, pageCount]);

  useEffect(() => {
    if (!parentEntityId) return;

    setLoading(true);

    // TODO: Move outside of the list page. Can be passed in
    AvalanchePathImagesService.getAvalanchePathImages(parentEntityId).then(
      (avalancheImages: AvalanchePathImagesDTO[]) => {
        setMediaEntities(avalancheImages);
        setLoading(false);
      },
    );
  }, [parentEntityId]);

  useEffect(() => {
    const myFilteredData = avalanchePathImagesService.filterData(mediaEntities, sortBy, search);
    const filteredPaginatedData = myFilteredData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    setFilteredData(myFilteredData);
    setPaginatedData(filteredPaginatedData);
  }, [mediaEntities, sortBy, search, pageIndex, pageSize]);

  // TODO: AvalanchePathImagesGrid and AvalanchePathImagesTable can be the same file. The only difference between them is the table or the cards
  return (
    <>
      {avalanchePathImagesService.getFilters({ sortBy, setSortBy, setSearchParams, search, setSearch })}
      {loading ? (
        <Row>
          <Col sm={12} className="text-center">
            <p>Loading...</p>
          </Col>
        </Row>
      ) : filteredData.length > 0 ? (
        <Row xs={1} md={2} className="g-4">
          {avalanchePathImagesService.getCards({
            setShowEditModal,
            setEntity,
            setShowDeleteModal,
            mediaEntities: paginatedData,
            setSortBy,
            mode,
          })}
        </Row>
      ) : (
        <Row>
          <Col sm={12} className="text-center">
            <p>No records found</p>
          </Col>
        </Row>
      )}
      <Row className="mb-3">
        <Col sm={12}>
          <Pagination
            {...{
              pageSize,
              setPageSize,
              rows: filteredData,
              setPageLoading,
              pageIndex,
              setPageIndex,
              canPreviousPage,
              canNextPage,
              pageCount,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            <Button
              className="mr-2 mr-mini-0 float-left"
              variant="primary"
              onClick={() => {
                setShowCreateModal(true);
              }}
              disabled={loading}
            >
              Create
            </Button>
          </Authorize>
        </Col>
      </Row>
      {showCreateModal && (
        <AvalanchePathImagesCreateModal
          visible={showCreateModal}
          onHide={onHideCreate}
          onSave={onHandleCreate}
          setEventErrors={setEventErrors}
        />
      )}
      {showEditModal && (
        <AvalanchePathImagesEditModal
          visible={showEditModal}
          entity={entity}
          mode={mode} // TODO: Split modal into View and Edit
          onHide={onHideEdit}
          onSave={onHandleEdit}
          onDelete={onDelete}
          setEventErrors={setEventErrors}
        />
      )}
      {showDeleteModal && (
        <AwpYesNoModal
          showYesNoModal={showDeleteModal}
          setShowYesNoModal={setShowDeleteModal}
          onClickYes={onDelete}
          title="Delete"
          body="Are you sure you want to delete?"
        />
      )}
      <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
    </>
  );
};

export default AvalanchePathImagesGrid;
